import {
  Button,
  ButtonBase,
  Card,
  Link as MuiLink,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core'
import { CheckBoxOutlineBlank, CheckBoxOutlined } from '@material-ui/icons'
import { FlexHorizontal, LoadMask, Progress, Spacer, useToasts } from '@vestaboard/installables'
import React, { useEffect } from 'react'

import { Alert } from '@material-ui/lab'
import { GetMasterPairing_getMasterPairing } from '../hooks/__generated__/GetMasterPairing'
import { Link } from '../../link'
import moment from 'moment'
import { useGetMasterPairing } from '../hooks/useGetMasterPairing'
import { useListDealAssociations } from '../hooks/useListDealAssociations'
import { useParams } from 'react-router'
import { useUpdateMasterPairing } from '../hooks/useUpdateMasterPairing'

export const EditMasterPairing = () => {
  const params = useParams<{ boardId: string; deviceId: string }>()
  const { data, loading, error } = useGetMasterPairing({
    input: {
      deviceId: params.deviceId,
      boardId: params.boardId
    }
  })
  const { data: dealAssociationData } = useListDealAssociations(
    {
      input: {
        dealId: data?.getMasterPairing?.__typename === 'MasterPairingV2' ? data?.getMasterPairing?.dealId : ''
      }
    },
    data?.getMasterPairing?.__typename !== 'MasterPairingV2' || !data?.getMasterPairing?.dealId
  )
  const { addToast } = useToasts()
  const [updateMasterPairing, { loading: saving }] = useUpdateMasterPairing()
  const [formValue, setFormValue] = React.useState<GetMasterPairing_getMasterPairing | null>(null)
  useEffect(() => {
    if (data?.getMasterPairing?.__typename === 'MasterPairingV2') {
      setFormValue(data.getMasterPairing)
    } else {
      setFormValue(null)
    }
  }, [data])
  const setValue = (key: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    if (formValue) {
      setFormValue({
        ...formValue,
        [key]: e.target.value
      })
    }
  }
  const handleSave = async () => {
    if (formValue?.__typename === 'MasterPairingV2') {
      const result = await updateMasterPairing({
        variables: {
          input: {
            deviceId: formValue?.deviceId || '',
            boardId: formValue?.boardId || '',
            manualReviewFlag: formValue?.manualReviewFlag || false,
            manualReviewMessage: formValue?.manualReviewMessage || '',
            synced: formValue?.synced || false,
            hubspotUpdated: formValue?.hubspotUpdated || false,
            hubspotMessage: formValue?.hubspotMessage || '',
            personFirstName: formValue?.personFirstName || '',
            personLastName: formValue?.personLastName || '',
            accountEmailAddress: formValue?.accountEmailAddress || '',
            accountId: formValue?.accountId || '',
            personId: formValue?.personId || '',
            memberIds: formValue?.memberIds || [],
            memberEmailAddresses: formValue?.memberEmailAddresses || [],
            pairingDate: formValue?.pairingDate ? moment(formValue?.pairingDate).unix() : null,
            plusApplicationDate: formValue?.plusApplicationDate ? moment(formValue?.plusApplicationDate).unix() : null,
            plusRenewalDate: formValue?.plusRenewalDate ? moment(formValue?.plusRenewalDate).unix() : null,
            plusRenewalFrequency: formValue?.plusRenewalFrequency || '',
            dealId: formValue?.dealId || ''
          }
        }
      })

      if (result?.data?.updateMasterPairing?.__typename === 'MasterPairingV2') {
        addToast('Master Pairing updated successfully.', {
          appearance: 'success',
          autoDismiss: true
        })
      } else {
        addToast('Failed to update Master Pairing.', {
          appearance: 'error',
          autoDismiss: true
        })
      }
    }
  }
  const pairing = formValue?.__typename === 'MasterPairingV2' ? formValue : null
  return error || data?.getMasterPairing?.__typename === 'MasterPairingErrorV2' ? (
    <Alert color='error'>Master Pairing not found.</Alert>
  ) : loading ? (
    <Progress />
  ) : (
    <>
      {saving ? <LoadMask /> : null}
      <FlexHorizontal>
        <Link to='/deal-associations/master-pairings'>
          <Typography variant='h4'>Master Pairings</Typography>
        </Link>
        <Typography variant='h4'> / {pairing?.dealId || 'No Deal ID'}</Typography>
      </FlexHorizontal>
      <Spacer size='extraLarge' />
      <Card>
        <TableContainer>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell style={{ width: 250 }}>Created At</TableCell>
                <TableCell>
                  <TextField
                    variant='outlined'
                    value={moment(pairing?.createdAt || '').format('MM/DD/YYYY')}
                    onChange={setValue('createdAt')}
                    fullWidth
                    disabled
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Synced</TableCell>
                <TableCell>
                  <ButtonBase
                    onClick={() => {
                      setFormValue({
                        ...formValue,
                        synced: !pairing?.synced
                      } as GetMasterPairing_getMasterPairing)
                    }}>
                    {pairing?.synced ? <CheckBoxOutlined /> : <CheckBoxOutlineBlank />}
                  </ButtonBase>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Manual Review Flag</TableCell>
                <TableCell>
                  <ButtonBase
                    onClick={() => {
                      setFormValue({
                        ...formValue,
                        manualReviewFlag: !pairing?.manualReviewFlag
                      } as GetMasterPairing_getMasterPairing)
                    }}>
                    {pairing?.manualReviewFlag ? <CheckBoxOutlined /> : <CheckBoxOutlineBlank />}
                  </ButtonBase>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Manual Review Message</TableCell>
                <TableCell>
                  <TextField
                    variant='outlined'
                    value={pairing?.manualReviewMessage || ''}
                    onChange={setValue('manualReviewMessage')}
                    fullWidth
                    multiline
                    minRows={3}
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
      <Spacer size='extraLarge' />
      <Typography variant='h5'>Board & Device</Typography>
      <Spacer size='medium' />
      <Card>
        <TableContainer>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell style={{ width: 250 }}>
                  <Link to={`/boards/${pairing?.boardId}`}>Board ID</Link>
                </TableCell>
                <TableCell>
                  <TextField
                    variant='outlined'
                    value={pairing?.boardId || ''}
                    onChange={setValue('boardId')}
                    fullWidth
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Board Title</TableCell>
                <TableCell>
                  <TextField
                    variant='outlined'
                    value={pairing?.boardTitle || ''}
                    onChange={setValue('boardTitle')}
                    fullWidth
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <Link to={`/devices/${pairing?.deviceId}`}>Device ID</Link>
                </TableCell>
                <TableCell>
                  <TextField
                    variant='outlined'
                    value={pairing?.deviceId || ''}
                    onChange={setValue('deviceId')}
                    fullWidth
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Pairing Date</TableCell>
                <TableCell>
                  <TextField
                    variant='outlined'
                    value={moment(new Date(pairing?.pairingDate || '')).format('MM/DD/YYYY')}
                    onChange={setValue('pairingDate')}
                    fullWidth
                    disabled
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
      <Spacer size='extraLarge' />
      <Typography variant='h5'>Account</Typography>
      <Spacer size='medium' />
      <Card>
        <TableContainer>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell style={{ width: 250 }}>Account ID</TableCell>
                <TableCell>
                  <TextField
                    variant='outlined'
                    value={pairing?.accountId || ''}
                    onChange={setValue('accountId')}
                    fullWidth
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <Link to={`/person/${pairing?.personId}`}>Person ID</Link>
                </TableCell>
                <TableCell>
                  <TextField
                    variant='outlined'
                    value={pairing?.personId || ''}
                    onChange={setValue('personId')}
                    fullWidth
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>First Name</TableCell>
                <TableCell>
                  <TextField
                    variant='outlined'
                    value={pairing?.personFirstName || ''}
                    onChange={setValue('personFirstName')}
                    fullWidth
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Last Name</TableCell>
                <TableCell>
                  <TextField
                    variant='outlined'
                    value={pairing?.personLastName || ''}
                    onChange={setValue('personLastName')}
                    fullWidth
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Email Address</TableCell>
                <TableCell>
                  <TextField
                    variant='outlined'
                    value={pairing?.accountEmailAddress || ''}
                    onChange={setValue('accountEmailAddress')}
                    fullWidth
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Member IDs</TableCell>
                <TableCell>
                  <TextField
                    variant='outlined'
                    value={pairing?.memberIds || ''}
                    onChange={setValue('memberIds')}
                    fullWidth
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Member Emails</TableCell>
                <TableCell>
                  <TextField
                    variant='outlined'
                    value={pairing?.memberEmailAddresses || ''}
                    onChange={e => {
                      const emails = e.target.value.split(',').map(email => email.trim())
                      setFormValue({
                        ...formValue,
                        memberEmailAddresses: emails
                      } as GetMasterPairing_getMasterPairing)
                    }}
                    fullWidth
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
      <Spacer size='extraLarge' />
      <Typography variant='h5'>Commerce</Typography>
      <Spacer size='medium' />
      <Card>
        <TableContainer>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell style={{ width: 250 }}>
                  {pairing?.dealId ? (
                    <a
                      href={`https://app.hubspot.com/contacts/1984131/record/0-3/${pairing?.dealId}`}
                      target='_blank'
                      rel='noopener noreferrer'
                      style={{
                        textDecoration: 'none'
                      }}>
                      <MuiLink>Deal ID</MuiLink>
                    </a>
                  ) : (
                    'Deal ID'
                  )}
                </TableCell>
                <TableCell>
                  <TextField variant='outlined' value={pairing?.dealId || ''} onChange={setValue('dealId')} fullWidth />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Plus Renewal Frequency</TableCell>
                <TableCell>
                  <TextField
                    variant='outlined'
                    value={pairing?.plusRenewalFrequency || ''}
                    onChange={setValue('plusRenewalFrequency')}
                    fullWidth
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Plus Application Date</TableCell>
                <TableCell>
                  <TextField
                    variant='outlined'
                    value={moment(pairing?.plusApplicationDate || '').format('MM/DD/YYYY')}
                    onChange={setValue('plusApplicationDate')}
                    fullWidth
                    disabled
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Plus Renewal Date</TableCell>
                <TableCell>
                  <TextField
                    variant='outlined'
                    value={moment(pairing?.plusRenewalDate || '').format('MM/DD/YYYY')}
                    onChange={setValue('plusRenewalDate')}
                    fullWidth
                    disabled
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Hubspot Updated?</TableCell>
                <TableCell>
                  <ButtonBase
                    onClick={() => {
                      setFormValue({
                        ...formValue,
                        hubspotUpdated: !pairing?.hubspotUpdated
                      } as GetMasterPairing_getMasterPairing)
                    }}>
                    {pairing?.hubspotUpdated ? <CheckBoxOutlined /> : <CheckBoxOutlineBlank />}
                  </ButtonBase>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Hubspot Message</TableCell>
                <TableCell>
                  <TextField
                    variant='outlined'
                    value={pairing?.hubspotMessage || ''}
                    onChange={setValue('hubspotMessage')}
                    fullWidth
                    multiline
                    minRows={3}
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
      <Spacer size='extraLarge' />
      <Typography variant='h5'>Deal Associations</Typography>
      <Spacer size='medium' />
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Email Address</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dealAssociationData?.listDealAssociations?.dealAssociations.map(association => (
                <TableRow key={association.id}>
                  <TableCell>{association.emailAddress}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
      <Spacer size='extraLarge' />
      <FlexHorizontal>
        <Button size='large' variant='contained' color='primary' onClick={handleSave}>
          Save
        </Button>
      </FlexHorizontal>
      <Spacer size='extraLarge' />
    </>
  )
}
